import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Sparkles, RefreshCw, Printer, FileText, History } from 'lucide-react';
import { CommunicationPanel } from '@/components/communication';
import { useToast } from '@/hooks/use-toast';
import PrintCandidateDialog from '@/components/candidate/PrintCandidateDialog';
import ResumeVersions from '@/components/candidate/ResumeVersions';
import ActivityLogDetail from '@/components/candidate/ActivityLogDetail';
import EditableAISummary from '@/components/candidate/EditableAISummary';
import ProjectsTab from '@/components/candidate/ProjectsTab';
import EditableContactInfo from '@/components/candidate/EditableContactInfo';
import EditableEducation from '@/components/candidate/EditableEducation';
import EditableExperience from '@/components/candidate/EditableExperience';
import EditableSkills from '@/components/candidate/EditableSkills';
import { Card } from '@/components/ui/card';
import { CandidateJobScore } from '@/components/scoring';

interface CandidateDetailDialogProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CandidateDetailDialog = ({ candidateId, open, onOpenChange }: CandidateDetailDialogProps) => {
  const [candidate, setCandidate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [resumeVersionsOpen, setResumeVersionsOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && candidateId) {
      fetchCandidateDetails();
      
      // Subscribe to parsing job completion to auto-refresh data
      const channel = supabase
        .channel(`candidate-parsing-${candidateId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'parsing_jobs',
            filter: `candidate_id=eq.${candidateId}`
          },
          (payload: any) => {
            // Refresh when parsing completes
            if (payload.new?.status === 'completed') {
              console.log('Parsing completed, refreshing candidate data');
              fetchCandidateDetails();
            }
          }
        )
        .subscribe();
      
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [open, candidateId]);

  const fetchCandidateDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidates')
        .select(`
          *,
          candidate_experience (
            *,
            companies (name, primary_location)
          ),
          candidate_education (*),
          candidate_skills (
            *,
            skills (name, type),
            experience_id
          ),
          candidate_applications (
            id,
            job_id,
            jobs (title),
            status,
            applied_at
          ),
          candidate_ai_insights (*),
          candidate_projects (
            *,
            companies:client_company_id (name)
          ),
          candidate_awards (*)
        `)
        .eq('id', candidateId)
        .single();

      if (error) throw error;
      setCandidate(data);
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAISummary = async () => {
    setGeneratingSummary(true);
    try {
      const { error } = await supabase.functions.invoke('generate-skills-summary', {
        body: { candidateId }
      });

      if (error) throw error;

      toast({ title: 'AI Summary generated successfully' });
      fetchCandidateDetails();
    } catch (error: any) {
      toast({ title: 'Error generating summary', description: error.message, variant: 'destructive' });
    } finally {
      setGeneratingSummary(false);
    }
  };

  const formatSalary = (min: number | null, max: number | null, currency: string | null) => {
    if (!min && !max) return null;
    const curr = currency || 'USD';
    if (min && max) return `${curr} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `${curr} ${min.toLocaleString()}+`;
    if (max) return `Up to ${curr} ${max.toLocaleString()}`;
    return null;
  };

  const getAvailabilityColor = (status: string | null) => {
    switch (status) {
      case 'available': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'open_to_offers': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'not_looking': return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
      default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  const candidateContext = candidate ? {
    id: candidateId,
    firstName: candidate.first_name || undefined,
    lastName: candidate.last_name || undefined,
    email: candidate.email_primary || undefined,
    phone: candidate.phone_primary || undefined,
    city: candidate.city || undefined,
  } : { id: candidateId };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : candidate ? (
          <div className="flex h-[85vh]">
            {/* Left: Candidate Profile */}
            <div className="flex-1 overflow-y-auto p-6">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-2xl">
                    {candidate.first_name} {candidate.last_name}
                  </DialogTitle>
                  {candidate.availability_status && (
                    <Badge className={getAvailabilityColor(candidate.availability_status)}>
                      {candidate.availability_status.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => setPrintOpen(true)}>
                    <Printer className="h-4 w-4 mr-1" />
                    Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setResumeVersionsOpen(true)}>
                    <FileText className="h-4 w-4 mr-1" />
                    Resumes
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setActivityLogOpen(true)}>
                    <History className="h-4 w-4 mr-1" />
                    History
                  </Button>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
              {/* Contact & Status Info - Editable */}
              <EditableContactInfo candidate={candidate} onUpdate={fetchCandidateDetails} />

              {/* AI Summaries Section */}
              <EditableAISummary
                candidateId={candidateId}
                skillsSummary={candidate.ai_skills_summary}
                productsSummary={candidate.ai_products_summary}
                onUpdate={fetchCandidateDetails}
              />

              <Tabs defaultValue="experience">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="experience">Experience</TabsTrigger>
                  <TabsTrigger value="projects">Projects</TabsTrigger>
                  <TabsTrigger value="skills">Skills</TabsTrigger>
                  <TabsTrigger value="education">Education</TabsTrigger>
                  <TabsTrigger value="applications">Applications</TabsTrigger>
                  <TabsTrigger value="insights">AI Insights</TabsTrigger>
                </TabsList>

                <TabsContent value="experience" className="space-y-3">
                  <EditableExperience
                    candidateId={candidateId}
                    experiences={candidate.candidate_experience || []}
                    onUpdate={fetchCandidateDetails}
                  />
                </TabsContent>

                <TabsContent value="projects" className="space-y-3">
                  <ProjectsTab candidateId={candidateId} />
                </TabsContent>

                <TabsContent value="skills" className="space-y-3">
                  <EditableSkills
                    candidateId={candidateId}
                    skills={candidate.candidate_skills || []}
                    onUpdate={fetchCandidateDetails}
                  />
                </TabsContent>

                <TabsContent value="education" className="space-y-3">
                  <EditableEducation
                    candidateId={candidateId}
                    education={candidate.candidate_education || []}
                    onUpdate={fetchCandidateDetails}
                  />
                </TabsContent>

                <TabsContent value="applications" className="space-y-3">
                  {candidate.candidate_applications?.length > 0 ? (
                    candidate.candidate_applications.map((app: any, i: number) => (
                      <Card key={i} className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{app.jobs?.title || 'Unknown Position'}</h4>
                            <p className="text-sm text-muted-foreground">
                              Applied on {new Date(app.applied_at).toLocaleDateString()}
                            </p>
                            <Badge variant="secondary" className="mt-2">{app.status}</Badge>
                          </div>
                          {app.job_id && (
                            <CandidateJobScore 
                              candidateId={candidateId} 
                              jobId={app.job_id} 
                              compact 
                            />
                          )}
                        </div>
                      </Card>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No applications</p>
                  )}
                </TabsContent>

                <TabsContent value="insights" className="space-y-3">
                  <div className="flex justify-end mb-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={generateAISummary}
                      disabled={generatingSummary}
                    >
                      {generatingSummary ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Generate AI Summary
                    </Button>
                  </div>
                  
                  {candidate.candidate_ai_insights && candidate.candidate_ai_insights.length > 0 ? (
                    <>
                      {candidate.candidate_ai_insights[0].summary && (
                        <Card className="p-4">
                          <h4 className="font-semibold mb-2">Summary</h4>
                          <p className="text-sm text-muted-foreground">{candidate.candidate_ai_insights[0].summary}</p>
                        </Card>
                      )}
                      
                      {candidate.candidate_ai_insights[0].strengths && (
                        <Card className="p-4">
                          <h4 className="font-semibold mb-2">Strengths</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidate.candidate_ai_insights[0].strengths}</p>
                        </Card>
                      )}
                      
                      {candidate.candidate_ai_insights[0].weaknesses && (
                        <Card className="p-4">
                          <h4 className="font-semibold mb-2">Areas for Development</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidate.candidate_ai_insights[0].weaknesses}</p>
                        </Card>
                      )}
                      
                      {candidate.candidate_ai_insights[0].role_fit_score && (
                        <Card className="p-4">
                          <h4 className="font-semibold mb-2">Role Fit Score</h4>
                          <div className="flex items-center gap-4">
                            <div className="text-3xl font-bold text-primary">
                              {candidate.candidate_ai_insights[0].role_fit_score}/10
                            </div>
                            <div className="flex-1">
                              <div className="w-full bg-secondary h-2 rounded-full">
                                <div 
                                  className="bg-primary h-2 rounded-full" 
                                  style={{ width: `${candidate.candidate_ai_insights[0].role_fit_score * 10}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}
                    </>
                  ) : (
                    <Card className="p-8 text-center">
                      <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground">No AI insights available yet</p>
                      <p className="text-sm text-muted-foreground mt-2">Click "Generate AI Summary" to create insights</p>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
              </div>
            </div>

            {/* Right: Communication Panel */}
            <div className="w-[400px] border-l bg-muted/30">
              <CommunicationPanel
                candidateId={candidateId}
                candidate={candidateContext}
                className="h-full border-0 rounded-none"
              />
            </div>
          </div>
        ) : (
          <p className="text-center py-8 p-6">Candidate not found</p>
        )}
      </DialogContent>

      {/* Print Dialog */}
      <PrintCandidateDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        candidate={candidate}
      />

      {/* Resume Versions Dialog */}
      <ResumeVersions
        open={resumeVersionsOpen}
        onOpenChange={setResumeVersionsOpen}
        candidateId={candidateId}
      />

      {/* Activity Log Dialog */}
      <ActivityLogDetail
        open={activityLogOpen}
        onOpenChange={setActivityLogOpen}
        candidateId={candidateId}
      />
    </Dialog>
  );
};

export default CandidateDetailDialog;
